import * as React from "react";
import { ForwardRef, isMemo } from "react-is";

const { isValidElement, version, useMemo } = React;
const safeVersion = typeof version === "string" && version ? version : "18.0.0";
const ReactMajorVersion = Number(safeVersion.split(".")[0]) || 18;

export const fillRef = (ref, node) => {
  if (typeof ref === "function") {
    ref(node);
  } else if (ref && typeof ref === "object" && "current" in ref) {
    ref.current = node;
  }
};

export const composeRef = (...refs) => {
  const refList = refs.filter(Boolean);
  if (refList.length <= 1) {
    return refList[0];
  }
  return (node) => {
    refList.forEach((ref) => {
      fillRef(ref, node);
    });
  };
};

export const useComposeRef = (...refs) =>
  useMemo(
    () => composeRef(...refs),
    refs,
    (prev, next) =>
      prev.length !== next.length || prev.every((ref, i) => ref !== next[i]),
  );

const isFragment = (node) => isValidElement(node) && node.type === React.Fragment;
const isReactElement = (node) => isValidElement(node) && !isFragment(node);

export const supportRef = (nodeOrComponent) => {
  if (!nodeOrComponent) return false;

  if (isReactElement(nodeOrComponent) && ReactMajorVersion >= 19) {
    return true;
  }
  const type = isMemo(nodeOrComponent)
    ? nodeOrComponent.type.type
    : nodeOrComponent.type;

  if (
    typeof type === "function" &&
    !(type.prototype && type.prototype.render) &&
    type.$$typeof !== ForwardRef
  ) {
    return false;
  }

  if (
    typeof nodeOrComponent === "function" &&
    !(nodeOrComponent.prototype && nodeOrComponent.prototype.render) &&
    nodeOrComponent.$$typeof !== ForwardRef
  ) {
    return false;
  }
  return true;
};

export const supportNodeRef = (node) => isReactElement(node) && supportRef(node);

export const getNodeRef = (node) => {
  if (node && isReactElement(node)) {
    const ele = node;
    return ele.props.propertyIsEnumerable("ref") ? ele.props.ref : ele.ref;
  }
  return null;
};
